/**
 * lib/notificationsNative.js — ramo NATIVO (Capacitor Android) delle push FCM.
 *
 * Nel guscio nativo il service worker web è inaffidabile (Android WebView):
 * qui usiamo il plugin @capacitor-firebase/messaging, che riceve le push via
 * l'SDK Firebase NATIVO di Android. Il backend è lo STESSO della PWA: registra
 * il token con il callable `registerFCMToken` marcandolo `platform:'android'`,
 * e il "campanello vuoto" server-side (data + notification generica, nessun
 * contenuto/mittente) resta identico.
 *
 * Il plugin viene importato dinamicamente SOLO qui e SOLO su nativo: sul web
 * questo modulo non tocca mai il plugin (isNativeApp() è false a monte).
 */
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

const DEVICE_ID_KEY = 'nullum.deviceId';
const FCM_TOKEN_KEY = 'nullum.fcmToken';
const NOTIF_WANTED_KEY = 'nullum.notifWanted.v1';
// DEVE combaciare con android.notification.channelId inviato dal server
// (functions/src/triggers/sendPushOnMessage.ts → 'nullum_messages').
const CHANNEL_ID = 'nullum_messages';

function getOrCreateDeviceId() {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

/**
 * Import lazy del plugin: carica il codice nativo solo quando serve davvero.
 * ⚠️ Ritorna un OGGETTO wrapper, NON direttamente `mod.FirebaseMessaging`: il
 * proxy di un plugin Capacitor è "thenable" (espone un metodo `then`), e se una
 * funzione async lo ritorna, la risoluzione della promise lo ADOTTA chiamando
 * `.then()` → sul nativo esplode con "FirebaseMessaging.then() is not implemented".
 * L'oggetto wrapper non è thenable, quindi `await fm()` risolve pulito.
 */
async function fm() {
  const mod = await import('@capacitor-firebase/messaging');
  return { FirebaseMessaging: mod.FirebaseMessaging };
}

async function registerToken(token) {
  const registerFn = httpsCallable(functions, 'registerFCMToken');
  await registerFn({ deviceId: getOrCreateDeviceId(), fcmToken: token, platform: 'android' });
  try {
    localStorage.setItem(FCM_TOKEN_KEY, token);
    localStorage.setItem(NOTIF_WANTED_KEY, '1');
  } catch {}
}

async function ensurePermission(FirebaseMessaging) {
  let perm = await FirebaseMessaging.checkPermissions();
  if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
    perm = await FirebaseMessaging.requestPermissions();
  }
  return perm.receive === 'granted';
}

async function ensureChannel(FirebaseMessaging) {
  // Android 8+: la notifica del server usa channelId 'nullum_messages'; se il
  // canale non esiste, la notifica ricade su un canale di default. Lo creiamo
  // idempotente con importanza alta (heads-up) e vibrazione.
  try {
    const { channels } = await FirebaseMessaging.listChannels();
    if (channels && channels.some((c) => c.id === CHANNEL_ID)) return;
  } catch { /* listChannels può non esistere su alcune versioni: proviamo a creare */ }
  try {
    await FirebaseMessaging.createChannel({
      id: CHANNEL_ID,
      name: 'Nuovi messaggi',
      description: 'Avviso di nuovo messaggio (senza contenuto)',
      importance: 5,   // MAX → heads-up
      visibility: 1,   // sulla lockscreen mostra il canale, non il contenuto
      vibration: true,
    });
  } catch (e) { console.warn('createChannel failed', e); }
}

/** Attiva le push native: permesso + canale + token + registrazione server. */
export async function enableNotificationsNative() {
  try {
    const { FirebaseMessaging } = await fm();
    const granted = await ensurePermission(FirebaseMessaging);
    if (!granted) return { ok: false, reason: 'PERMISSION_DENIED' };
    await ensureChannel(FirebaseMessaging);
    const { token } = await FirebaseMessaging.getToken();
    if (!token) return { ok: false, reason: 'NO_TOKEN' };
    await registerToken(token);
    return { ok: true, token };
  } catch (err) {
    console.error('enableNotificationsNative failed', err);
    return { ok: false, reason: 'ERROR', error: err?.message || String(err) };
  }
}

/** Self-healing nativo: re-arma token + registrazione se l'utente le voleva. */
export async function ensureNotificationsHealthyNative() {
  try {
    if (localStorage.getItem(NOTIF_WANTED_KEY) !== '1') return { ok: false, reason: 'NOT_WANTED' };
    const { FirebaseMessaging } = await fm();
    const perm = await FirebaseMessaging.checkPermissions();
    if (perm.receive !== 'granted') return { ok: false, reason: 'NO_PERMISSION' };
    return await enableNotificationsNative();
  } catch (e) {
    return { ok: false, reason: 'ERROR', error: e?.message };
  }
}

/** Disattiva le push native e cancella il token server-side. */
export async function disableNotificationsNative() {
  const deviceId = localStorage.getItem(DEVICE_ID_KEY);
  if (deviceId) {
    try {
      const unregisterFn = httpsCallable(functions, 'unregisterFCMToken');
      await unregisterFn({ deviceId });
    } catch (err) { console.warn('unregisterFCMToken failed', err); }
  }
  try {
    const { FirebaseMessaging } = await fm();
    await FirebaseMessaging.deleteToken();
  } catch {}
  try {
    localStorage.removeItem(FCM_TOKEN_KEY);
    localStorage.removeItem(NOTIF_WANTED_KEY);
  } catch {}
  return { ok: true };
}

/**
 * Listener nativi. In foreground/background il plugin scatta `notificationReceived`
 * (per i data-message); il tap sulla notifica di sistema scatta
 * `notificationActionPerformed`. `tokenReceived` gestisce la rotazione del token.
 * Ritorna una funzione di cleanup.
 */
export async function installForegroundMessageListenerNative(callback) {
  try {
    const { FirebaseMessaging } = await fm();
    const subs = [];

    const onIncoming = (data) => {
      try {
        window.dispatchEvent(new CustomEvent('nullum:fcm-foreground', {
          detail: { data: data || {}, notification: {} },
        }));
      } catch {}
      try { callback(data || {}); } catch {}
      try { if (navigator.vibrate) navigator.vibrate([120, 60, 120]); } catch {}
    };

    subs.push(await FirebaseMessaging.addListener('notificationReceived', (event) => {
      onIncoming(event?.notification?.data || {});
    }));
    subs.push(await FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
      // Tap sulla notifica: l'OS porta l'app in foreground; noi refreshiamo.
      onIncoming(event?.notification?.data || {});
    }));
    subs.push(await FirebaseMessaging.addListener('tokenReceived', async (event) => {
      try {
        if (event?.token) await registerToken(event.token);
      } catch (e) { console.warn('native token refresh failed', e); }
    }));

    return () => { for (const s of subs) { try { s.remove(); } catch {} } };
  } catch (err) {
    console.error('installForegroundMessageListenerNative failed', err);
    return () => {};
  }
}

export function areNotificationsEnabledNative() {
  try {
    return !!localStorage.getItem(FCM_TOKEN_KEY) && localStorage.getItem(NOTIF_WANTED_KEY) === '1';
  } catch { return false; }
}
